import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  CreditCard,
  SlidersHorizontal,
  Receipt,
  FileText,
  Download,
  Eye,
  EyeOff,
  ShieldCheck,
  IndianRupee,
  Clock,
  CheckCircle2,
  AlertCircle,
  XCircle,
} from 'lucide-react'
import { api, extractApiError } from '../../lib/api'
import {
  paymentSettings as paymentSettingsSchema,
  transactionsResponse,
  invoicesResponse,
} from '../../lib/types'
import { downloadInvoicePdf } from '../../lib/invoice-pdf'
import { DataTable } from '../../components/table/DataTable'
import { RowActionsMenu } from '../../components/table/RowActionsMenu'
import { FilterRow } from '../../components/filters/FilterRow'
import { Button } from '../../components/ui/Button'
import { TextField, FieldLabel } from '../../components/ui/Field'
import { formatDateTime, formatMoney, formatNumber } from '../../lib/format'
import { usePageSize } from '../../lib/display-prefs'

const inputBase =
  'w-full h-10 px-3 rounded-lg border border-[color:var(--color-rule)] ' +
  'bg-[color:var(--color-card)] text-[color:var(--color-ink)] text-sm ' +
  'placeholder:text-[color:var(--color-ink-subtle)] outline-none ' +
  'transition-colors focus:border-[color:var(--color-accent)]'

/** Per-tone tile + halo bleed for the Payments KPI strip. Same
 *  palette as the other 5 platform-admin list pages (Companies /
 *  Plans / All Users / AI Usage / Storage) so all six surfaces
 *  share one visual vocabulary. */
const KPI_TONES = {
  violet: {
    tile: 'bg-[#EDE9FE] text-[#7C3AED] border border-[#DDD6FE] dark:bg-white/[0.06] dark:text-violet-300 dark:border-white/10',
    halo: 'from-[#EDE9FE] to-transparent dark:from-transparent',
  },
  emerald: {
    tile: 'bg-[#D1FAE5] text-[#059669] border border-[#A7F3D0] dark:bg-white/[0.06] dark:text-emerald-300 dark:border-white/10',
    halo: 'from-[#D1FAE5] to-transparent dark:from-transparent',
  },
  amber: {
    tile: 'bg-[#FFEDD5] text-[#EA580C] border border-[#FED7AA] dark:bg-white/[0.06] dark:text-orange-300 dark:border-white/10',
    halo: 'from-[#FFEDD5] to-transparent dark:from-transparent',
  },
  sky: {
    tile: 'bg-[#DBEAFE] text-[#2563EB] border border-[#BFDBFE] dark:bg-white/[0.06] dark:text-sky-300 dark:border-white/10',
    halo: 'from-[#DBEAFE] to-transparent dark:from-transparent',
  },
  rose: {
    tile: 'bg-[#FFE4E6] text-[#E11D48] border border-[#FECDD3] dark:bg-white/[0.06] dark:text-rose-300 dark:border-white/10',
    halo: 'from-[#FFE4E6] to-transparent dark:from-transparent',
  },
}

/** Single-stat KPI card matching the shape used across the other
 *  platform-admin pages — rounded-xl chrome, blurred halo bleed
 *  top-right, 12×12 tinted icon chip, 3xl font-display value,
 *  uppercase tracking-wider label, optional muted hint underneath.
 *  Accepts both numeric and pre-formatted string values (the
 *  Payments page renders "₹1,200" via fmtMoney before passing
 *  the value in). */
function KpiCard({ label, value, icon: Icon, tone = 'violet', hint }) {
  const tones = KPI_TONES[tone] ?? KPI_TONES.violet
  return (
    <div className="flex flex-col relative overflow-hidden rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5 min-h-[120px]">
      <div
        aria-hidden="true"
        className={
          'pointer-events-none absolute -top-12 -right-10 h-36 w-36 rounded-full blur-2xl opacity-70 bg-gradient-to-br ' +
          tones.halo
        }
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-sm uppercase tracking-wider text-[color:var(--color-ink-muted)] font-medium m-0">
            {label}
          </h3>
          <p className="mt-2 font-display text-3xl text-[color:var(--color-ink-strong)] tabular-nums leading-none">
            {typeof value === 'number'
              ? formatNumber(value)
              : value}
          </p>
          {hint ? (
            <p className="mt-2 text-[13px] text-[color:var(--color-ink-muted)]">
              {hint}
            </p>
          ) : null}
        </div>
        <div
          className={
            'h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ' +
            tones.tile
          }
        >
          <Icon size={24} />
        </div>
      </div>
    </div>
  )
}

function fmtDateTime(iso) {
  if (!iso) return '—'
  return formatDateTime(iso)
}

export function PaymentsPage() {
  const [tab, setTab] = useState('gateway')

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
          <CreditCard
            size={20}
            className="text-[color:var(--color-accent-strong)]"
          />
          Payments &amp; Billing
        </h1>
        <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
          Set up payments and view transactions.
        </p>
      </div>

      <div className="flex items-center gap-1 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-0.5 w-fit mb-5">
        <TabButton
          active={tab === 'gateway'}
          onClick={() => setTab('gateway')}
          icon={SlidersHorizontal}
          label="Gateway Settings"
        />
        <TabButton
          active={tab === 'transactions'}
          onClick={() => setTab('transactions')}
          icon={Receipt}
          label="Transactions"
        />
        <TabButton
          active={tab === 'invoices'}
          onClick={() => setTab('invoices')}
          icon={FileText}
          label="Invoices"
        />
      </div>

      {tab === 'gateway' ? (
        <GatewayTab />
      ) : tab === 'transactions' ? (
        <TransactionsTab />
      ) : (
        <InvoicesTab />
      )}
    </div>
  )
}

function TabButton({ active, onClick, icon: Icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium cursor-pointer transition-colors ' +
        (active
          ? 'bg-[color:var(--color-primary)] text-white'
          : 'text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)]')
      }
    >
      <Icon size={14} />
      {label}
    </button>
  )
}

/** Pill on/off switch. */
function Toggle({ checked, onChange, disabled, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={
        'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed ' +
        (checked
          ? 'bg-[color:var(--color-primary)]'
          : 'bg-[color:var(--color-rule-strong)]')
      }
    >
      <span
        className={
          'inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ' +
          (checked ? 'translate-x-4' : 'translate-x-0.5')
        }
      />
    </button>
  )
}

/* ----------------------------- Gateway tab ------------------------------ */

function GatewayTab() {
  const query = useQuery({
    queryKey: ['payment-settings'],
    queryFn: async () => {
      const res = await api.get('/super-admin/payment-settings')
      return paymentSettingsSchema.parse(res.data)
    },
  })

  if (query.isLoading) {
    return <p className="text-sm text-[color:var(--color-ink-muted)]">Loading…</p>
  }
  if (query.isError) {
    return <ErrorBlock message={extractApiError(query.error).message} />
  }
  // Remount on fresh data so the form re-seeds cleanly after a save.
  return <GatewayForm key={query.dataUpdatedAt} settings={query.data} />
}

function GatewayForm({ settings }) {
  const qc = useQueryClient()
  const [form, setForm] = useState(() => ({
    razorpayKeyId: settings.razorpayKeyId,
    razorpayKeySecret: '',
    razorpayWebhookSecret: '',
    mode: settings.mode,
    enabled: settings.enabled,
  }))
  const [showSecret, setShowSecret] = useState(false)

  function set(key, value) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body = {
        razorpayKeyId: form.razorpayKeyId.trim(),
        mode: form.mode,
        enabled: form.enabled,
      }
      // Empty secret fields are omitted — the server keeps the stored value.
      if (form.razorpayKeySecret) {
        body.razorpayKeySecret = form.razorpayKeySecret
      }
      if (form.razorpayWebhookSecret) {
        body.razorpayWebhookSecret = form.razorpayWebhookSecret
      }
      const res = await api.put('/super-admin/payment-settings', body)
      return res.data
    },
    onSuccess: () => {
      toast.success('Payment settings saved successfully.')
      void qc.invalidateQueries({ queryKey: ['payment-settings'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (!saveMutation.isPending) saveMutation.mutate()
      }}
      className="max-w-2xl"
    >
      <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] overflow-hidden">
        {/* Card header */}
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-[color:var(--color-rule)] bg-[color:var(--color-canvas)]">
          <div className="flex items-center gap-2.5">
            <span className="grid place-items-center h-9 w-9 rounded-lg bg-[#FEF3C7] border border-[#FDE68A] dark:bg-white/[0.06] dark:border-white/10">
              <ShieldCheck
                size={17}
                className="text-[#D97706] dark:text-amber-300"
              />
            </span>
            <div>
              <p className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
                Razorpay
              </p>
              <p className="text-xs text-[color:var(--color-ink-muted)]">
                Accept card, UPI and netbanking payments.
              </p>
            </div>
          </div>
          <span
            className={
              'text-xs uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ' +
              (form.enabled
                ? 'bg-[color:var(--color-success-bg)] text-[color:var(--color-success)] border-[color:var(--color-success-border)]'
                : 'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border-[color:var(--color-rule)]')
            }
          >
            {form.enabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>

        {/* Card body */}
        <div className="px-5 py-5 space-y-4">
          <TextField
            label="Razorpay Key ID"
            placeholder="Enter the Razorpay Key ID"
            maxLength={120}
            value={form.razorpayKeyId}
            onChange={(e) => set('razorpayKeyId', e.target.value)}
            helperText="The publishable key — safe to use in the checkout widget."
          />

          {/* Secret Key — masked with show/hide */}
          <div>
            <FieldLabel label="Razorpay Secret Key" htmlFor="rzp-secret" />
            <div className="relative">
              <input
                id="rzp-secret"
                type={showSecret ? 'text' : 'password'}
                value={form.razorpayKeySecret}
                onChange={(e) => set('razorpayKeySecret', e.target.value)}
                maxLength={400}
                autoComplete="new-password"
                placeholder={
                  settings.hasKeySecret
                    ? '•••••••••••••• (leave blank to keep current)'
                    : 'Enter Razorpay Secret Key'
                }
                className={inputBase + ' pr-10'}
              />
              <button
                type="button"
                onClick={() => setShowSecret((s) => !s)}
                aria-label={showSecret ? 'Hide secret key' : 'Show secret key'}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
              >
                {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
              Stored encrypted — it is never shown again after saving.
            </p>
          </div>

          {/* Webhook Secret */}
          <div>
            <FieldLabel
              label="Razorpay Webhook Secret"
              htmlFor="rzp-webhook"
            />
            <input
              id="rzp-webhook"
              type="text"
              value={form.razorpayWebhookSecret}
              onChange={(e) => set('razorpayWebhookSecret', e.target.value)}
              maxLength={400}
              autoComplete="off"
              placeholder={
                settings.hasWebhookSecret
                  ? '•••••••••••••• (leave blank to keep current)'
                  : 'Enter Razorpay Webhook Secret'
              }
              className={inputBase}
            />
            <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
              Used to verify incoming Razorpay webhook signatures.
            </p>
          </div>

          {/* Mode segmented control */}
          <div>
            <FieldLabel label="Environment" htmlFor="rzp-mode" />
            <div
              id="rzp-mode"
              className="inline-flex items-center gap-0.5 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] p-0.5"
            >
              {[
                { v: 'test', label: 'Test Mode' },
                { v: 'live', label: 'Live Mode' },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => set('mode', opt.v)}
                  className={
                    'px-3.5 py-1.5 rounded-md text-sm font-medium cursor-pointer transition-colors ' +
                    (form.mode === opt.v
                      ? 'bg-[color:var(--color-primary)] text-white'
                      : 'text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)]')
                  }
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
              {form.mode === 'live'
                ? 'Live mode — real payments will be charged.'
                : 'Test mode — use Razorpay test keys; no real money moves.'}
            </p>
          </div>

          {/* Enable toggle */}
          <div className="flex items-center justify-between gap-4 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-4 py-3">
            <div>
              <p className="text-sm font-medium text-[color:var(--color-ink-strong)]">
                Enable Razorpay
              </p>
              <p className="text-xs text-[color:var(--color-ink-muted)]">
                Turn the gateway on to accept payments platform-wide.
              </p>
            </div>
            <Toggle
              checked={form.enabled}
              onChange={(next) => set('enabled', next)}
              label="Enable Razorpay"
            />
          </div>
        </div>

        {/* Card footer */}
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-[color:var(--color-rule)] bg-[color:var(--color-canvas)]">
          <span className="text-xs text-[color:var(--color-ink-subtle)]">
            {settings.updatedAt
              ? `Last updated ${fmtDateTime(settings.updatedAt)}`
              : 'Not configured yet.'}
          </span>
          <Button type="submit" loading={saveMutation.isPending}>
            Save Settings
          </Button>
        </div>
      </div>
    </form>
  )
}

/* --------------------------- Transactions tab --------------------------- */

const TXN_STATUS_PILL = {
  paid: 'bg-[color:var(--color-success-bg)] text-[color:var(--color-success)] border border-[color:var(--color-success-border)]',
  created:
    'bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)] border border-[color:var(--color-warning-border)]',
  failed:
    'bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger)] border border-[color:var(--color-danger-border)]',
}

const TXN_STATUS_LABEL = {
  paid: 'Paid',
  created: 'Pending',
  failed: 'Failed',
}

/** The transaction's own currency symbol + the person's number format. */
function fmtMoney(amount, currency) {
  return formatMoney(Number(amount ?? 0), currency || 'INR')
}

function TransactionsTab() {
  const [page, setPage] = useState(1)
  // Starts at (and follows) the account rows-per-page (My Preferences);
  // this list's API accepts every standard size up to 500.
  const [limit, setLimit] = usePageSize()
  // Free-text search across tenant name, plan name, and Razorpay
  // identifiers. Server-side so the filter applies across ALL pages,
  // not just the current one.
  const [search, setSearch] = useState('')
  const query = useQuery({
    queryKey: ['transactions', page, limit, search],
    queryFn: async () => {
      const params = { page, limit }
      if (search.trim()) params.search = search.trim()
      const res = await api.get('/super-admin/transactions', { params })
      return transactionsResponse.parse(res.data)
    },
    placeholderData: (prev) => prev,
  })
  // Reset to page 1 whenever the search changes so the result set
  // starts at the top rather than stranding the admin on a now-empty
  // page 4.
  useEffect(() => {
    setPage(1)
  }, [search])

  const rows = query.data?.data ?? []
  const pageInfo = query.data?.pagination ?? null
  const summary = query.data?.summary
  const currency = summary?.currency ?? 'INR'

  async function fetchAllForExport() {
    const all = []
    for (let p = 1; p <= 50; p += 1) {
      const params = { page: p, limit: 500 }
      if (search.trim()) params.search = search.trim()
      const res = await api.get('/super-admin/transactions', { params })
      const parsed = transactionsResponse.parse(res.data)
      all.push(...(parsed.data ?? []))
      const info = parsed.pagination
      if (!info || p >= info.totalPages) break
    }
    return all
  }

  const columns = useMemo(
    () => [
      {
        key: 'company',
        header: 'Company',
        grow: true,
        pinnable: true,
        minWidth: 180,
        sortAccessor: (r) => (r.tenantName ?? r.companyName ?? '').toLowerCase(),
        exportValue: (r) => r.tenantName ?? '',
        cellClassName: 'font-semibold text-[color:var(--color-ink-strong)]',
        render: (r) => r.tenantName ?? '—',
      },
      {
        key: 'plan',
        header: 'Plan',
        minWidth: 140,
        sortAccessor: (r) => (r.planName ?? r.planTier ?? '').toLowerCase(),
        exportValue: (r) => `${r.planName ?? r.planCode ?? ''} · ${r.cadence ?? ''}`,
        cellClassName: 'text-[color:var(--color-ink-muted)]',
        render: (r) => (
          <>
            {r.planName ?? r.planCode}
            <span className="text-[color:var(--color-ink-subtle)]"> · {r.cadence}</span>
          </>
        ),
      },
      {
        key: 'amount',
        header: 'Amount',
        minWidth: 110,
        sortAccessor: (r) => Number(r.amount ?? 0),
        exportValue: (r) => fmtMoney(r.amount, r.currency),
        cellClassName:
          'font-medium text-[color:var(--color-ink-strong)] tabular-nums',
        render: (r) => fmtMoney(r.amount, r.currency),
      },
      {
        key: 'status',
        header: 'Status',
        minWidth: 110,
        sortAccessor: (r) => r.status ?? '',
        exportValue: (r) => TXN_STATUS_LABEL[r.status] ?? r.status ?? '',
        render: (r) => (
          <span
            className={
              'inline-flex w-fit text-xs uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ' +
              (TXN_STATUS_PILL[r.status] ?? TXN_STATUS_PILL.created)
            }
          >
            {TXN_STATUS_LABEL[r.status] ?? r.status}
          </span>
        ),
      },
      {
        key: 'createdAt',
        header: 'Date',
        align: 'right',
        minWidth: 150,
        sortAccessor: (r) => (r.createdAt ? new Date(r.createdAt) : null),
        exportValue: (r) => fmtDateTime(r.createdAt),
        cellClassName:
          'text-[color:var(--color-ink-muted)] whitespace-nowrap tabular-nums',
        render: (r) => fmtDateTime(r.createdAt),
      },
    ],
    [],
  )

  const cards = [
    {
      label: 'Total Revenue',
      value: summary ? fmtMoney(summary.totalRevenue, currency) : '—',
      icon: IndianRupee,
      tone: 'violet',
      hint: 'Lifetime captured payments',
    },
    {
      label: 'Successful Payments',
      value: summary ? String(summary.successfulCount) : '—',
      icon: CheckCircle2,
      tone: 'emerald',
    },
    {
      label: 'Pending Payments',
      value: summary ? String(summary.pendingCount) : '—',
      icon: Clock,
      tone: 'amber',
      hint: 'Awaiting customer action',
    },
    {
      label: 'Failed Payments',
      value: summary ? String(summary.failedCount ?? 0) : '—',
      icon: XCircle,
      tone: 'rose',
      hint: 'Card declined / abandoned',
    },
  ]

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        {cards.map((c) => (
          <KpiCard
            key={c.label}
            label={c.label}
            value={c.value}
            icon={c.icon}
            tone={c.tone}
            hint={c.hint}
          />
        ))}
      </div>

      <FilterRow>
        <FilterRow.Search
          value={search}
          onChange={setSearch}
          placeholder="Search by Company, Plan or Razorpay ID"
        />
      </FilterRow>

      <DataTable
        tableId="platform-transactions"
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        loading={query.isLoading}
        fetching={query.isFetching}
        error={query.isError ? extractApiError(query.error).message : null}
        onRetry={() => query.refetch()}
        searchable={false}
        title="Recent Transactions"
        defaultSort={[{ key: 'createdAt', dir: 'desc' }]}
        page={pageInfo?.page ?? page}
        totalPages={pageInfo?.totalPages ?? 1}
        total={pageInfo?.total ?? 0}
        limit={pageInfo?.limit ?? limit}
        onPageChange={setPage}
        onLimitChange={(n) => {
          setLimit(n)
          setPage(1)
        }}
        exportBaseName="transactions"
        exportTitle="Transactions"
        getExportRows={fetchAllForExport}
        emptyState={{
          icon: Receipt,
          title: 'No transactions yet',
          body: 'Payments processed through Razorpay will be listed here.',
        }}
      />
    </div>
  )
}

/* ------------------------------ Invoices tab ---------------------------- */

function InvoiceStatusPill({ status }) {
  const paid = (status || 'paid') === 'paid'
  return (
    <span
      className={
        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ' +
        (paid
          ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-white/[0.06] dark:text-emerald-300 dark:border-white/10'
          : 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-white/[0.06] dark:text-slate-300 dark:border-white/10')
      }
    >
      {paid ? 'Paid' : 'Void'}
    </span>
  )
}

function InvoicesTab() {
  const [page, setPage] = useState(1)
  // Starts at (and follows) the account rows-per-page (My Preferences);
  // this list's API accepts every standard size up to 500.
  const [limit, setLimit] = usePageSize()
  const [search, setSearch] = useState('')
  const query = useQuery({
    queryKey: ['sa-invoices', page, limit, search],
    queryFn: async () => {
      const params = { page, limit }
      if (search.trim()) params.search = search.trim()
      const res = await api.get('/super-admin/invoices', { params })
      return invoicesResponse.parse(res.data)
    },
    placeholderData: (prev) => prev,
  })
  useEffect(() => {
    setPage(1)
  }, [search])

  const rows = query.data?.invoices ?? []
  const pageInfo = query.data?.pagination ?? null

  async function fetchAllForExport() {
    const all = []
    for (let p = 1; p <= 50; p += 1) {
      const params = { page: p, limit: 500 }
      if (search.trim()) params.search = search.trim()
      const res = await api.get('/super-admin/invoices', { params })
      const parsed = invoicesResponse.parse(res.data)
      all.push(...(parsed.invoices ?? []))
      const info = parsed.pagination
      if (!info || p >= info.totalPages) break
    }
    return all
  }

  const columns = useMemo(
    () => [
      {
        key: 'invoiceNumber',
        header: 'Invoice No.',
        pinnable: true,
        minWidth: 140,
        sortAccessor: (i) => (i.invoiceNumber ?? '').toLowerCase(),
        exportValue: (i) => i.invoiceNumber ?? '',
        cellClassName:
          'font-medium text-[color:var(--color-ink-strong)] tabular-nums whitespace-nowrap',
        render: (i) => i.invoiceNumber,
      },
      {
        key: 'tenantName',
        header: 'Company',
        grow: true,
        minWidth: 180,
        sortAccessor: (i) => (i.tenantName ?? '').toLowerCase(),
        exportValue: (i) => i.tenantName ?? '',
        cellClassName: 'text-[color:var(--color-ink)]',
        render: (i) => <span className="truncate block">{i.tenantName}</span>,
      },
      {
        key: 'plan',
        header: 'Plan',
        minWidth: 130,
        sortAccessor: (i) => (i.planName ?? i.planCode ?? '').toLowerCase(),
        exportValue: (i) => i.planName || i.planCode || '',
        cellClassName: 'text-[color:var(--color-ink-muted)]',
        render: (i) => i.planName || i.planCode || '—',
      },
      {
        key: 'amount',
        header: 'Amount',
        minWidth: 110,
        sortAccessor: (i) => Number(i.total ?? i.amount ?? 0),
        exportValue: (i) => fmtMoney(i.total ?? i.amount, i.currency),
        cellClassName:
          'tabular-nums text-[color:var(--color-ink)] whitespace-nowrap',
        render: (i) => fmtMoney(i.total ?? i.amount, i.currency),
      },
      {
        key: 'status',
        header: 'Status',
        minWidth: 100,
        sortAccessor: (i) => i.status ?? '',
        exportValue: (i) => ((i.status || 'paid') === 'paid' ? 'Paid' : 'Void'),
        render: (i) => <InvoiceStatusPill status={i.status} />,
      },
      {
        key: 'issuedAt',
        header: 'Issued',
        minWidth: 150,
        sortAccessor: (i) => (i.issuedAt ? new Date(i.issuedAt) : null),
        exportValue: (i) => fmtDateTime(i.issuedAt),
        cellClassName:
          'text-[color:var(--color-ink-muted)] whitespace-nowrap',
        render: (i) => fmtDateTime(i.issuedAt),
      },
      {
        key: 'actions',
        // Singular: this column renders exactly ONE row action. Restore the
        // plural if a second is ever added — the eleven columns that already
        // carry two or more use "Actions".
        header: 'Action',
        pinnedRight: true,
        align: 'center',
        minWidth: 72,
        sortable: false,
        exportable: false,
        hideable: false,
        render: (i) => (
          <RowActionsMenu
            label={`Actions for invoice ${i.invoiceNumber ?? ''}`}
            items={[
              {
                key: 'pdf',
                label: 'Download PDF',
                icon: Download,
                onClick: () => downloadInvoicePdf(i),
              },
            ]}
          />
        ),
      },
    ],
    [],
  )

  return (
    <div>
      <FilterRow>
        <FilterRow.Search
          value={search}
          onChange={setSearch}
          placeholder="Search by Invoice No., Company or Email"
        />
      </FilterRow>

      <DataTable
        tableId="platform-invoices"
        columns={columns}
        rows={rows}
        getRowId={(i) => i._id}
        loading={query.isLoading}
        fetching={query.isFetching}
        error={query.isError ? extractApiError(query.error).message : null}
        onRetry={() => query.refetch()}
        searchable={false}
        title="Invoices"
        defaultSort={[{ key: 'issuedAt', dir: 'desc' }]}
        page={pageInfo?.page ?? page}
        totalPages={pageInfo?.totalPages ?? 1}
        total={pageInfo?.total ?? 0}
        limit={pageInfo?.limit ?? limit}
        onPageChange={setPage}
        onLimitChange={(n) => {
          setLimit(n)
          setPage(1)
        }}
        exportBaseName="invoices"
        exportTitle="Invoices"
        getExportRows={fetchAllForExport}
        emptyState={{ icon: FileText, title: 'No invoices yet' }}
      />
    </div>
  )
}

/* ------------------------------- Shared --------------------------------- */

function ErrorBlock({ message }) {
  return (
    <div className="rounded-xl border border-[color:var(--color-danger-border)] bg-[color:var(--color-danger-bg)] p-6 flex items-start gap-3">
      <AlertCircle
        size={18}
        className="shrink-0 mt-0.5 text-[color:var(--color-danger)]"
      />
      <div>
        <p className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
          Couldn't load payment settings
        </p>
        <p className="mt-0.5 text-xs text-[color:var(--color-ink-muted)]">
          {message}
        </p>
      </div>
    </div>
  )
}
